import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import ReserveSpotModal from '../components/ReserveSpotModal';
import BookingModal from '../components/BookingModal';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:3001';

const Home = () => {
    const [reserveModalOpen, setReserveModalOpen] = useState(false);
    const [selectedTour, setSelectedTour] = useState(null);
    const [bookingModalOpen, setBookingModalOpen] = useState(false);
    const [selectedTourId, setSelectedTourId] = useState(null);
    const [selectedTourName, setSelectedTourName] = useState('');
    const [selectedTourPrice, setSelectedTourPrice] = useState('');
    const [featuredTours, setFeaturedTours] = useState([]);
    const [currentTours, setCurrentTours] = useState([]);
    const [homeCategories, setHomeCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    const openReserveModal = (tour) => {
        setSelectedTour(tour);
        setReserveModalOpen(true);
    };

    const openBookingModal = (tourName, price, tourId = null) => {
        setSelectedTourName(tourName);
        setSelectedTourPrice(price || '');
        setSelectedTourId(tourId);
        setBookingModalOpen(true);
    };

    // Fetch tours from API
    useEffect(() => {
        const fetchTours = async () => {
            try {
                setLoading(true);
                // Fetch featured tours, current tours, and categories in parallel
                const [featuredResponse, currentToursResponse, categoriesResponse] = await Promise.all([
                    axios.get('/api/tours?featured=true&limit=6'),
                    axios.get('/api/tours?limit=6&hasAvailableDates=true'),
                    axios.get('/api/categories')
                ]);
                setFeaturedTours(featuredResponse.data.tours);
                setCurrentTours(currentToursResponse.data.tours);
                setHomeCategories(categoriesResponse.data.slice(0, 4));
            } catch (error) {
                console.error('Error fetching tours:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchTours();
    }, []);

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0
        }).format(price);
    };

    const getTourImage = (tour) => {
        if (tour.images && tour.images.length > 0) {
            const img = tour.images[0];
            // If it's a relative path (starts with /), prefix with API URL
            if (img.startsWith('/')) {
                return `${API_URL}${img}`;
            }
            return img;
        }
        return 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&h=600&fit=crop';
    };

    const getBadge = (tour) => {
        if (tour.isFeatured && tour.tags?.includes('bestseller')) return 'BESTSELLER';
        if (tour.tags?.includes('hot deal')) return 'HOT DEAL';
        if (tour.category === 'luxury') return 'LUXURY';
        return null;
    };

    return (
        <>
            <div>
                <Navigation />

                {/* Hero Section */}
                <section className="relative mt-20 overflow-hidden" style={{ minHeight: '600px', height: '600px' }}>
                    <div className="blob bg-brand-green w-96 h-96 top-20 left-10"></div>
                    <div className="blob bg-brand-teal w-80 h-80 bottom-20 right-20" style={{ animationDelay: '2s' }}></div>
                    <div className="blob bg-brand-cyan w-72 h-72 top-40 right-40" style={{ animationDelay: '4s' }}></div>

                    <div className="absolute inset-0">
                        <img src="https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1920&h=1080&fit=crop" alt="Travel Hero" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue/80 to-brand-teal/60"></div>
                    </div>

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
                        <div className="text-white max-w-3xl">
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight text-glow">Discover Your Next Adventure</h1>
                            <p className="text-lg sm:text-xl mb-6 text-gray-200">Explore breathtaking destinations around the world with our expertly curated travel packages</p>

                            <div className="flex flex-col sm:flex-row gap-4 mb-6">
                                <Link to="/tours" className="bg-gradient-to-r from-brand-green to-brand-teal text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-2xl transition transform hover:scale-110 pulse-animation text-center">
                                    <i className="fas fa-search mr-2"></i>Explore Tours
                                </Link>
                                <Link to="/categories" className="bg-white text-brand-blue px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition transform hover:scale-110 hover:shadow-2xl text-center">
                                    <i className="fas fa-list mr-2"></i>View Packages
                                </Link>
                            </div>

                            <div className="bg-white rounded-2xl p-4 shadow-2xl card-shine">
                                <form onSubmit={(e) => { e.preventDefault(); }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div className="transform transition hover:scale-105">
                                        <label className="text-gray-600 text-sm font-medium mb-2 block">Destination</label>
                                        <input type="text" id="quickDestination" placeholder="Where to?" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal text-gray-800 transition" />
                                    </div>
                                    <div className="transform transition hover:scale-105">
                                        <label className="text-gray-600 text-sm font-medium mb-2 block">Date</label>
                                        <input type="date" id="quickDate" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal text-gray-800 transition" />
                                    </div>
                                    <div className="transform transition hover:scale-105">
                                        <label className="text-gray-600 text-sm font-medium mb-2 block">Travelers</label>
                                        <select id="quickTravelers" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal text-gray-800 transition">
                                            <option>1 Person</option>
                                            <option>2 People</option>
                                            <option>3-5 People</option>
                                            <option>6+ People</option>
                                        </select>
                                    </div>
                                    <div className="flex items-end">
                                        <button type="submit" className="w-full bg-gradient-to-r from-brand-teal to-brand-cyan text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition transform hover:scale-105">
                                            <i className="fas fa-search mr-2"></i>Search
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>

                    <div className="absolute top-32 right-20 float-animation hidden lg:block">
                        <i className="fas fa-plane text-white text-4xl opacity-20"></i>
                    </div>
                    <div className="absolute bottom-40 left-32 float-animation-delayed hidden lg:block">
                        <i className="fas fa-map-marked-alt text-white text-5xl opacity-20"></i>
                    </div>
                </section>

                {/* Stats Counter Section */}
                <section className="py-8 bg-gradient-to-r from-brand-teal to-brand-cyan relative overflow-hidden">
                    <div className="absolute inset-0 opacity-10">
                        <div className="blob bg-white w-96 h-96 top-0 left-0"></div>
                        <div className="blob bg-white w-80 h-80 bottom-0 right-0" style={{ animationDelay: '3s' }}></div>
                    </div>
                   
                </section>

                {/* Tour Types */}
                <section className="py-20 bg-white relative overflow-hidden" id="types">
                    <div className="absolute inset-0 opacity-5">
                        <div className="blob bg-brand-green w-96 h-96 top-20 right-20"></div>
                        <div className="blob bg-brand-teal w-80 h-80 bottom-20 left-20" style={{ animationDelay: '2s' }}></div>
                    </div>
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl sm:text-5xl font-bold text-gray-800 mb-4">Explore by Tour Type</h2>
                            <p className="text-xl text-gray-600">Choose your perfect travel experience</p>
                        </div>

                        {homeCategories.length === 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                                {[...Array(4)].map((_, i) => (
                                    <div key={i} className="h-80 rounded-2xl bg-gray-200 animate-pulse"></div>
                                ))}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                                {homeCategories.map((cat) => (
                                    <Link
                                        key={cat.id || cat._id}
                                        to={`/tours?category=${cat.id || cat._id}`}
                                        className="group cursor-pointer hover-lift"
                                    >
                                        <div className="relative h-80 rounded-2xl overflow-hidden shadow-lg hover-zoom">
                                            <img
                                                src={cat.image ? (cat.image.startsWith('/') ? `${API_URL}${cat.image}` : cat.image) : `https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=600&h=800&fit=crop`}
                                                alt={cat.name}
                                                className="w-full h-full object-cover transition duration-700"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                                            <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform transition-all duration-300 group-hover:translate-y-0 translate-y-2">
                                                <h3 className="text-2xl font-bold mb-2">{cat.name}</h3>
                                                <p className="text-gray-200 opacity-0 group-hover:opacity-100 transition-opacity duration-300">{cat.description?.substring(0, 60)}</p>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                        <div className="text-center mt-8">
                            <Link to="/categories" className="inline-block bg-gradient-to-r from-brand-teal to-brand-cyan text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transition transform hover:scale-110">
                                View All Categories <i className="fas fa-arrow-right ml-2"></i>
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Featured Tour Packages */}
                <section className="py-20 bg-gray-50 relative overflow-hidden" id="packages">
                    <div className="absolute inset-0 opacity-5">
                        <div className="blob bg-brand-green w-96 h-96 top-40 left-40"></div>
                        <div className="blob bg-brand-cyan w-80 h-80 bottom-40 right-40" style={{ animationDelay: '3s' }}></div>
                    </div>
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl sm:text-5xl font-bold text-gray-800 mb-4">Featured Tour Packages</h2>
                            <p className="text-xl text-gray-600">Handpicked destinations for unforgettable experiences</p>
                        </div>

                        {loading ? (
                            <div className="text-center py-12">
                                <i className="fas fa-spinner fa-spin text-4xl text-brand-teal mb-4"></i>
                                <p className="text-gray-600">Loading tours...</p>
                            </div>
                        ) : featuredTours.length === 0 ? (
                            <div className="text-center py-12">
                                <p className="text-gray-600">No featured tours available</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {featuredTours.map((tour) => {
                                    const badge = getBadge(tour);
                                    return (
                                        <div key={tour.id || tour._id} className="bg-white rounded-2xl overflow-hidden shadow-lg hover-lift card-shine">
                                            <div className="relative h-64 overflow-hidden hover-zoom">
                                                <img src={getTourImage(tour)} alt={tour.title} className="w-full h-full object-cover transition duration-700" />
                                                {badge && (
                                                    <div className={`absolute top-4 left-4 ${badge === 'HOT DEAL' ? 'bg-red-500' : badge === 'LUXURY' ? 'bg-gradient-to-r from-brand-green to-brand-teal' : 'bg-brand-cyan'} text-white px-4 py-2 rounded-full font-semibold text-sm wave-animation`}>
                                                        {badge}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="p-6">
                                                <div className="flex items-center justify-between mb-3">
                                                    <span className="text-sm text-gray-500"><i className="fas fa-clock mr-2"></i>{tour.duration.days} Days / {tour.duration.nights} Nights</span>

                                                </div>
                                                <h3 className="text-2xl font-bold text-gray-800 mb-2">{tour.title}</h3>
                                                <p className="text-gray-600 mb-4">{tour.description.substring(0, 100)}...</p>
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                                                        <i className="fas fa-map-marker-alt"></i>
                                                        <span>{tour.destination}, {tour.country.charAt(0).toUpperCase() + tour.country.slice(1)}</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                                                    <div>
                                                        <span className="text-gray-500 text-sm">From</span>
                                                        <div className="text-3xl font-bold bg-gradient-to-r from-brand-teal to-brand-cyan bg-clip-text text-transparent">{formatPrice(tour.price)}</div>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <Link
                                                            to={`/tours/${tour.id || tour._id}`}
                                                            className="bg-white border-2 border-brand-teal text-brand-teal px-4 py-3 rounded-full font-semibold hover:bg-brand-teal hover:text-white transition transform hover:scale-110"
                                                        >
                                                            <i className="fas fa-info-circle mr-2"></i>Details
                                                        </Link>
                                                        <button
                                                            onClick={() => openBookingModal(tour.title, formatPrice(tour.price), tour.id || tour._id)}
                                                            className="bg-gradient-to-r from-brand-teal to-brand-cyan text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transition transform hover:scale-110"
                                                        >
                                                            Book Now
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <div className="text-center mt-12">
                            <Link to="/tours" className="inline-block bg-white text-brand-teal border-2 border-brand-teal px-8 py-4 rounded-full text-lg font-semibold hover:bg-brand-teal hover:text-white transition transform hover:scale-110 hover:shadow-xl">
                                View All Tours <i className="fas fa-arrow-right ml-2"></i>
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Current Tours */}
                <section className="py-20 bg-white relative overflow-hidden" id="tours">
                    <div className="absolute inset-0 opacity-5">
                        <div className="blob bg-brand-green w-96 h-96 top-20 left-20"></div>
                        <div className="blob bg-brand-teal w-80 h-80 bottom-20 right-20" style={{ animationDelay: '4s' }}></div>
                    </div>
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl sm:text-5xl font-bold text-gray-800 mb-4">Current Tours Departing Soon</h2>
                            <p className="text-xl text-gray-600">Join these exciting tours with limited availability</p>
                        </div>

                        {loading ? (
                            <div className="text-center py-12">
                                <i className="fas fa-spinner fa-spin text-4xl text-brand-teal mb-4"></i>
                                <p className="text-gray-600">Loading current tours...</p>
                            </div>
                        ) : currentTours.length === 0 ? (
                            <div className="text-center py-12">
                                <p className="text-gray-600">No current tours available</p>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {currentTours.map((tour) => {
                                    const nextDate = tour.availableDates && tour.availableDates.length > 0
                                        ? tour.availableDates[0]
                                        : null;
                                    const daysUntil = nextDate
                                        ? Math.ceil((new Date(nextDate.startDate) - new Date()) / (1000 * 60 * 60 * 24))
                                        : null;
                                    const spotsAvailable = nextDate ? nextDate.spotsAvailable : 0;

                                    return (
                                        <div key={tour.id || tour._id} className="bg-gradient-to-r from-green-50 to-teal-50 rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] card-shine">
                                            <div className="flex flex-col lg:flex-row gap-6">
                                                <div className="lg:w-1/3 hover-zoom rounded-xl overflow-hidden">
                                                    <img src={getTourImage(tour)} alt={tour.title} className="w-full h-64 object-cover rounded-xl transition duration-700" />
                                                </div>
                                                <div className="lg:w-2/3 flex flex-col justify-between">
                                                    <div>
                                                        <div className="flex flex-wrap items-center gap-3 mb-4">
                                                            {daysUntil !== null && daysUntil > 0 && (
                                                                <span className="bg-red-500 text-white px-4 py-1 rounded-full text-sm font-semibold pulse-animation">
                                                                    Departing in {daysUntil} {daysUntil === 1 ? 'Day' : 'Days'}
                                                                </span>
                                                            )}
                                                            {spotsAvailable > 0 && (
                                                                <span className="bg-gradient-to-r from-brand-green to-brand-teal text-white px-4 py-1 rounded-full text-sm font-semibold wave-animation">
                                                                    {spotsAvailable} {spotsAvailable === 1 ? 'Spot' : 'Spots'} {spotsAvailable <= 3 ? 'Left' : 'Available'}
                                                                </span>
                                                            )}
                                                            {nextDate && (
                                                                <span className="text-gray-600">
                                                                    <i className="fas fa-calendar-alt mr-2"></i>
                                                                    {new Date(nextDate.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(nextDate.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <h3 className="text-3xl font-bold text-gray-800 mb-3">{tour.title}</h3>
                                                        <p className="text-gray-600 mb-4 text-lg">{tour.description.substring(0, 150)}...</p>
                                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                                                            <div className="flex items-center text-gray-600 transform hover:scale-110 transition">
                                                                <i className="fas fa-clock text-brand-teal mr-2"></i>
                                                                <span>{tour.duration.days} Days</span>
                                                            </div>
                                                            <div className="flex items-center text-gray-600 transform hover:scale-110 transition">
                                                                <i className="fas fa-users text-brand-teal mr-2"></i>

                                                            </div>
                                                            <div className="flex items-center text-gray-600 transform hover:scale-110 transition">
                                                                <i className="fas fa-map-marker-alt text-brand-teal mr-2"></i>
                                                                <span>{tour.destination}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-gray-300">
                                                        <div>
                                                            <span className="text-gray-500 text-sm">Starting from</span>
                                                            <div className="text-4xl font-bold bg-gradient-to-r from-brand-teal to-brand-cyan bg-clip-text text-transparent">{formatPrice(tour.price)}</div>
                                                            <span className="text-gray-500 text-sm">per person</span>
                                                        </div>
                                                        <button
                                                            onClick={() => openReserveModal({
                                                                name: tour.title,
                                                                price: formatPrice(tour.price),
                                                                spotsAvailable: spotsAvailable,
                                                                departureDate: nextDate ? `${new Date(nextDate.startDate).toLocaleDateString()} - ${new Date(nextDate.endDate).toLocaleDateString()}` : 'TBD',
                                                                duration: `${tour.duration.days} Days`
                                                            })}
                                                            className="bg-gradient-to-r from-brand-teal to-brand-cyan text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transition transform hover:scale-110 pulse-animation"
                                                        >
                                                            Reserve Your Spot
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </section>

                {/* Villas & Bungalows Teaser */}
                <section className="py-20 bg-gray-50 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-5">
                        <div className="blob bg-brand-teal w-96 h-96 top-20 left-20"></div>
                        <div className="blob bg-brand-green w-80 h-80 bottom-20 right-20" style={{ animationDelay: '3s' }}></div>
                    </div>
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="flex flex-col lg:flex-row gap-12 items-center">
                            <div className="lg:w-1/2">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="rounded-2xl overflow-hidden shadow-lg hover-zoom h-48">
                                        <img src="https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=600&h=400&fit=crop" alt="Villa" className="w-full h-full object-cover transition duration-700" />
                                    </div>
                                    <div className="rounded-2xl overflow-hidden shadow-lg hover-zoom h-48 mt-8">
                                        <img src="https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=600&h=400&fit=crop" alt="Bungalow" className="w-full h-full object-cover transition duration-700" />
                                    </div>
                                    <div className="rounded-2xl overflow-hidden shadow-lg hover-zoom h-48 -mt-8">
                                        <img src="https://images.unsplash.com/photo-1613977257363-707ba9348227?w=600&h=400&fit=crop" alt="Pool Villa" className="w-full h-full object-cover transition duration-700" />
                                    </div>
                                    <div className="rounded-2xl overflow-hidden shadow-lg hover-zoom h-48">
                                        <img src="https://images.unsplash.com/photo-1602343168117-bb8ffe3e2e9f?w=600&h=400&fit=crop" alt="Bungalow Interior" className="w-full h-full object-cover transition duration-700" />
                                    </div>
                                </div>
                            </div>
                            <div className="lg:w-1/2">
                                <span className="text-brand-teal font-semibold uppercase tracking-wider text-sm">Exclusive Properties</span>
                                <h2 className="text-4xl sm:text-5xl font-bold text-gray-800 mt-2 mb-6">Villas & Bungalows</h2>
                                <p className="text-lg text-gray-600 mb-6">Escape to privacy and luxury with our curated collection of private villas and charming bungalows. Perfect for families, couples, and groups seeking an exclusive home-away-from-home experience.</p>
                                <div className="grid grid-cols-2 gap-4 mb-8">
                                    {[
                                        { icon: 'fa-home', label: 'Private Properties' },
                                        { icon: 'fa-swimming-pool', label: 'Premium Amenities' },
                                        { icon: 'fa-users', label: 'Groups Welcome' },
                                        { icon: 'fa-concierge-bell', label: 'Concierge Service' },
                                    ].map((f, i) => (
                                        <div key={i} className="flex items-center gap-3 text-gray-700">
                                            <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0">
                                                <i className={`fas ${f.icon} text-brand-teal`}></i>
                                            </div>
                                            <span className="font-medium">{f.label}</span>
                                        </div>
                                    ))}
                                </div>
                                <Link to="/villas" className="inline-block bg-gradient-to-r from-brand-teal to-brand-cyan text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transition transform hover:scale-110">
                                    <i className="fas fa-home mr-2"></i>Browse Villas & Bungalows
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Why Choose Us */}

                <section className="py-20 gradient-animate text-white relative overflow-hidden">
                    <div className="absolute inset-0 opacity-10">
                        <div className="blob bg-white w-96 h-96 top-20 left-20"></div>
                        <div className="blob bg-white w-80 h-80 bottom-20 right-20" style={{ animationDelay: '3s' }}></div>
                    </div>
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl sm:text-5xl font-bold mb-4">Why Choose VishwaVoyagerz?</h2>
                            <p className="text-xl text-green-100">Your trusted partner for unforgettable travel experiences</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                            <div className="text-center transform hover:scale-110 transition duration-300">
                                <div className="bg-white/10 backdrop-blur-lg w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 float-animation">
                                    <i className="fas fa-shield-alt text-4xl"></i>
                                </div>
                                <h3 className="text-2xl font-bold mb-3">Safe & Secure</h3>
                                <p className="text-green-100">Your safety is our priority with 24/7 support and comprehensive travel insurance</p>
                            </div>

                            <div className="text-center transform hover:scale-110 transition duration-300">
                                <div className="bg-white/10 backdrop-blur-lg w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 float-animation-delayed">
                                    <i className="fas fa-award text-4xl"></i>
                                </div>
                                <h3 className="text-2xl font-bold mb-3">Award Winning</h3>
                                <p className="text-green-100">Recognized globally for excellence in travel services and customer satisfaction</p>
                            </div>

                            <div className="text-center transform hover:scale-110 transition duration-300">
                                <div className="bg-white/10 backdrop-blur-lg w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 float-animation">
                                    <i className="fas fa-dollar-sign text-4xl"></i>
                                </div>
                                <h3 className="text-2xl font-bold mb-3">Best Prices</h3>
                                <p className="text-green-100">Competitive rates with no hidden fees and flexible payment options available</p>
                            </div>

                            <div className="text-center transform hover:scale-110 transition duration-300">
                                <div className="bg-white/10 backdrop-blur-lg w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 float-animation-delayed">
                                    <i className="fas fa-headset text-4xl"></i>
                                </div>
                                <h3 className="text-2xl font-bold mb-3">24/7 Support</h3>
                                <p className="text-green-100">Round-the-clock customer service to assist you before, during, and after your trip</p>
                            </div>
                        </div>
                    </div>
                </section>

            

                <Footer />

                {/* Booking Modal */}
                <div id="bookingModal" className="modal">
                    <div className="modal-content bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4">
                        <div className="bg-gradient-to-r from-brand-teal to-brand-cyan text-white p-6 rounded-t-2xl">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h3 className="text-3xl font-bold mb-2">Book Your Tour</h3>
                                    <p id="modalTourName" className="text-green-100"></p>
                                </div>
                                <button className="text-white hover:text-gray-200 text-3xl transform hover:rotate-90 transition">
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        </div>

                        <form className="p-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">Full Name *</label>
                                    <input type="text" id="bookingName" required="" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal" />
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">Email Address *</label>
                                    <input type="email" id="bookingEmail" required="" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal" />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">Phone Number *</label>
                                    <input type="tel" id="bookingPhone" required="" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal" />
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">Number of Travelers *</label>
                                    <select id="bookingTravelers" required="" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal">
                                        <option value="">Select...</option>
                                        <option value="1">1 Person</option>
                                        <option value="2">2 People</option>
                                        <option value="3">3 People</option>
                                        <option value="4">4 People</option>
                                        <option value="5+">5+ People</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">Preferred Start Date *</label>
                                    <input type="date" id="bookingDate" required="" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal" />
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">Country</label>
                                    <input type="text" id="bookingCountry" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal" />
                                </div>
                            </div>

                            <div className="mb-6">
                                <label className="block text-gray-700 font-semibold mb-2">Special Requests or Questions</label>
                                <textarea id="bookingMessage" rows="4" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal" placeholder="Tell us about any special requirements, dietary restrictions, or questions you have..."></textarea>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4">
                                <button type="submit" className="flex-1 bg-gradient-to-r from-brand-teal to-brand-cyan text-white px-8 py-4 rounded-full font-semibold hover:shadow-xl transition transform hover:scale-105">
                                    <i className="fas fa-paper-plane mr-2"></i>Submit Booking Request
                                </button>
                                <button type="button" className="flex-1 bg-gray-200 text-gray-700 px-8 py-4 rounded-full font-semibold hover:bg-gray-300 transition">
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Contact Modal */}
                <div id="contactModal" className="modal">
                    <div className="modal-content bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4">
                        <div className="bg-gradient-to-r from-brand-green to-brand-teal text-white p-6 rounded-t-2xl">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h3 className="text-3xl font-bold mb-2">Contact Us</h3>
                                    <p className="text-green-100">We'd love to hear from you</p>
                                </div>
                                <button className="text-white hover:text-gray-200 text-3xl transform hover:rotate-90 transition">
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        </div>

                        <form className="p-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">Your Name *</label>
                                    <input type="text" id="contactName" required="" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal" />
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">Email Address *</label>
                                    <input type="email" id="contactEmail" required="" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal" />
                                </div>
                            </div>

                            <div className="mb-6">
                                <label className="block text-gray-700 font-semibold mb-2">Subject *</label>
                                <input type="text" id="contactSubject" required="" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal" />
                            </div>

                            <div className="mb-6">
                                <label className="block text-gray-700 font-semibold mb-2">Message *</label>
                                <textarea id="contactMessage" rows="5" required="" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal" placeholder="How can we help you?"></textarea>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4">
                                <button type="submit" className="flex-1 bg-gradient-to-r from-brand-green to-brand-teal text-white px-8 py-4 rounded-full font-semibold hover:shadow-xl transition transform hover:scale-105">
                                    <i className="fas fa-paper-plane mr-2"></i>Send Message
                                </button>
                                <button type="button" className="flex-1 bg-gray-200 text-gray-700 px-8 py-4 rounded-full font-semibold hover:bg-gray-300 transition">
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Video Modal */}
                <div id="videoModal" className="modal">
                    <div className="modal-content bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4">
                        <div className="bg-gradient-to-r from-brand-cyan to-brand-blue text-white p-6 rounded-t-2xl">
                            <div className="flex justify-between items-center">
                                <h3 className="text-3xl font-bold">Explore the World with VishwaVoyagerz</h3>
                                <button className="text-white hover:text-gray-200 text-3xl transform hover:rotate-90 transition">
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        </div>
                        <div className="p-8">
                            <div className="aspect-video bg-gray-900 rounded-lg flex items-center justify-center">
                                <div className="text-center text-white">
                                    <i className="fas fa-video text-6xl mb-4 opacity-50"></i>
                                    <p className="text-xl">Video player would be embedded here</p>
                                    <p className="text-sm text-gray-400 mt-2">YouTube or Vimeo video integration</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Success Modal */}
                <div id="successModal" className="modal">
                    <div className="modal-content bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 text-center p-8">
                        <div className="mb-6">
                            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <i className="fas fa-check text-4xl text-green-500"></i>
                            </div>
                            <h3 className="text-3xl font-bold text-gray-800 mb-2">Success!</h3>
                            <p id="successMessage" className="text-gray-600 text-lg"></p>
                        </div>
                        <button className="bg-gradient-to-r from-brand-teal to-brand-cyan text-white px-8 py-3 rounded-full font-semibold hover:shadow-xl transition transform hover:scale-105">
                            Close
                        </button>
                    </div>
                </div>

                <ReserveSpotModal
                    isOpen={reserveModalOpen}
                    onClose={() => setReserveModalOpen(false)}
                    tour={selectedTour}
                />

                <BookingModal
                    isOpen={bookingModalOpen}
                    onClose={() => setBookingModalOpen(false)}
                    tourName={selectedTourName}
                    tourPrice={selectedTourPrice}
                    tourId={selectedTourId}
                />
            </div>
        </>
    );
};

export default Home;
